import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Yandex.Metrica integration with SPA-aware pageview tracking.
 *
 * Mount once at the top of <BrowserRouter> so the counter is loaded on
 * first paint and every subsequent in-app route change registers as its
 * own pageview (so `/de/products` and `/it/products` appear as distinct
 * pages in Metrica, which Yandex's ranking algo weighs heavily).
 *
 * Counter ID comes from REACT_APP_YANDEX_METRICA_ID, silently no-ops
 * when the env var is empty (e.g. preview, local dev) so we don't pollute
 * the counter with non-prod traffic.
 *
 * Loads asynchronously (`async=1`); zero impact on TTI / LCP.
 */
const YandexMetrica = () => {
  const counterId = process.env.REACT_APP_YANDEX_METRICA_ID;
  const location = useLocation();
  const initialised = useRef(false);

  // One-time counter init on mount.
  useEffect(() => {
    if (!counterId || initialised.current) return;
    initialised.current = true;

    // Standard Yandex.Metrica loader snippet, ported to ES2020 syntax.
    (function (m, e, t, r, i) {
      m[i] = m[i] || function () { (m[i].a = m[i].a || []).push(arguments); };
      m[i].l = 1 * new Date();
      // Idempotency guard, refuse to double-inject if HMR re-runs the effect.
      for (let j = 0; j < e.scripts.length; j++) {
        if (e.scripts[j].src === r) return;
      }
      const k = e.createElement(t);
      const a = e.getElementsByTagName(t)[0];
      k.async = 1;
      k.src = r;
      a.parentNode.insertBefore(k, a);
    })(window, document, 'script', 'https://mc.yandex.ru/metrika/tag.js', 'ym');

    // `webvisor` records anonymous click/scroll heatmaps, required for
    // the engagement-quality signals that Yandex uses to rank EU SERPs.
    window.ym(Number(counterId), 'init', {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true,
      defer: false,
      ecommerce: 'dataLayer',
    });
  }, [counterId]);

  // SPA pageview on every route change. The initial pageview is recorded
  // by `init` above, so skip the first effect run to avoid double-counting.
  const prevPath = useRef(null);
  useEffect(() => {
    if (!counterId || !window.ym) return;
    const fullPath = location.pathname + location.search;
    if (prevPath.current === null) {
      // First render, already counted by init.
      prevPath.current = fullPath;
      return;
    }
    if (prevPath.current === fullPath) return;
    prevPath.current = fullPath;
    window.ym(Number(counterId), 'hit', fullPath, {
      title: document.title,
      referer: window.location.origin + prevPath.current,
    });
  }, [counterId, location.pathname, location.search]);

  return null;
};

export default YandexMetrica;
